// Environment configuration for TellerWand
// This file centralizes all environment variable handling

import {
  requireNonEmptyEnv,
  resolveSupabaseRuntimeCredentials,
  validateApiUrl,
  validateSupabaseUrl,
} from "./environmentConfigHelpers";

interface EnvironmentConfig {
  // Supabase configuration
  supabase: {
    url: string;
    anonKey: string;
    realtimeUrl?: string;
  };

  // API configuration
  api: {
    url: string;
  };

  // Optional integrations
  stripe?: {
    publicKey?: string;
  };

  sentry?: {
    dsn?: string;
  };
}

// Validate and extract environment variables
/**
 * Retrieves and validates environment configuration variables.
 * @example
 * getEnvironmentConfig()
 * Returns an object containing the configuration for Supabase, API, Stripe, and Sentry.
 * @returns {EnvironmentConfig} An object containing the configuration for Supabase, API, Stripe, and Sentry.
 */
const viteEnv = import.meta.env ?? ({} as Record<string, string | boolean | undefined>);

/**
 * Get Environment Config.
 */
const getEnvironmentConfig = (): EnvironmentConfig => {
  const {
    supabaseUrl: rawSupabaseUrl,
    supabaseAnonKey: rawSupabaseAnonKey,
    runtimeEnv,
  } = resolveSupabaseRuntimeCredentials(viteEnv);

  const supabaseUrl = requireNonEmptyEnv(rawSupabaseUrl, "VITE_SUPABASE_URL");
  const supabaseAnonKey = requireNonEmptyEnv(rawSupabaseAnonKey, "VITE_SUPABASE_ANON_KEY");

  const apiUrl = runtimeEnv?.VITE_API_URL || viteEnv.VITE_API_URL || "https://tellerwand.com";
  const realtimeUrl = viteEnv.VITE_REALTIME_URL;
  const stripePublicKey = viteEnv.VITE_STRIPE_PUBLIC_KEY;
  const sentryDsn = runtimeEnv?.VITE_SENTRY_DSN || viteEnv.VITE_SENTRY_DSN;

  const finalApiUrl = apiUrl || "https://tellerwand.com";

  validateSupabaseUrl(supabaseUrl);
  validateApiUrl(finalApiUrl);

  return {
    supabase: {
      url: supabaseUrl,
      anonKey: supabaseAnonKey,
      realtimeUrl,
    },
    api: {
      url: finalApiUrl,
    },
    stripe: stripePublicKey
      ? {
          publicKey: stripePublicKey,
        }
      : undefined,
    sentry: sentryDsn
      ? {
          dsn: sentryDsn,
        }
      : undefined,
  };
};

// Lazy initialization - only evaluate when accessed
// This ensures window.__ENV__ is available from config.js before evaluation
let cachedEnv: EnvironmentConfig | null = null;

const getEnv = (): EnvironmentConfig => {
  if (!cachedEnv) {
    cachedEnv = getEnvironmentConfig();
  }
  return cachedEnv;
};

// Export the validated configuration (lazy getter)
export const env = new Proxy({} as EnvironmentConfig, {
  get(_target, prop) {
    return getEnv()[prop as keyof EnvironmentConfig];
  },
});

// Export individual values as lazy Proxies (maintains value semantics)
// These work as strings but only evaluate when accessed
/**
 * Create Lazy String.
 */
const createLazyString = (getter: () => string): string => {
  return new Proxy({} as string, {
    get(_target, prop) {
      const value = getter();
      if (prop === "valueOf" || prop === Symbol.toPrimitive) {
        return () => value;
      }
      if (prop === "toString") {
        return () => value;
      }
      // For string methods, bind to the actual string value
      const strValue = String(value);
      const strObj = Object(strValue);
      const method = strObj[prop as keyof typeof strObj];
      if (typeof method === "function") {
        return method.bind(strValue);
      }
      return strObj[prop as keyof typeof strObj];
    },
    valueOf() {
      return getter();
    },
    toString() {
      return getter();
    },
    [Symbol.toPrimitive](hint: string) {
      return hint === "number" ? Number(getter()) : getter();
    },
  }) as unknown as string;
};

export const SUPABASE_URL = createLazyString(() => getEnv().supabase.url);
export const SUPABASE_ANON_KEY = createLazyString(() => getEnv().supabase.anonKey);
export const API_URL = createLazyString(() => getEnv().api.url);

// Development helper (lazy - only runs when accessed)
if (viteEnv.DEV) {
  // Delay logging until after config might be loaded
  setTimeout(() => {
    try {
      const envConfig = getEnv();
      console.warn("🔧 Environment Configuration:", {
        supabaseUrl: envConfig.supabase.url,
        apiUrl: envConfig.api.url,
        hasRealtime: !!envConfig.supabase.realtimeUrl,
        hasStripe: !!envConfig.stripe,
        hasSentry: !!envConfig.sentry,
      });
    } catch {
      // Silently fail in dev - config might not be ready yet
    }
  }, 100);
}
